import { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'

import { cn } from '@/utils/cn'
import { inputClass, labelClass } from './shared/modalStyles'

interface ClientsDropdownProps {
  clientNameToId: Record<string, number>
  selectedIds: Set<number>
  onChange: (ids: Set<number>) => void
  disabled?: boolean
}

const ClientsDropdown = ({
  clientNameToId,
  selectedIds,
  onChange,
  disabled = false,
}: ClientsDropdownProps) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const toggle = (id: number) => {
    const next = new Set(selectedIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange(next)
  }

  return (
    <div ref={ref} className='relative'>
      <label className={cn(labelClass, disabled && 'opacity-40')}>
        {disabled ? 'Clients' : 'Clients *'}
        {!disabled && selectedIds.size > 0 && (
          <span className='ml-1.5 font-normal text-text-tertiary normal-case'>
            ({selectedIds.size} selected)
          </span>
        )}
      </label>
      <button
        type='button'
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          inputClass,
          'flex items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-40',
        )}
      >
        <span className={cn(selectedIds.size === 0 ? 'text-text-dimmed' : 'text-text-primary')}>
          {selectedIds.size === 0
            ? 'Select clients…'
            : `${selectedIds.size} client${selectedIds.size > 1 ? 's' : ''} selected`}
        </span>
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-text-tertiary transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && !disabled && (
        <div className='border-border-main bg-neutral-dimmed absolute bottom-full z-10 mb-1 max-h-48 w-full overflow-y-auto rounded-lg border py-1 shadow-xl'>
          {Object.entries(clientNameToId).map(([name, id]) => (
            <label
              key={id}
              className='hover:bg-neutral-dimmed-heavy flex cursor-pointer items-center gap-3 px-3 py-2'
            >
              <input
                type='checkbox'
                checked={selectedIds.has(id)}
                onChange={() => toggle(id)}
                className='h-4 w-4 rounded border-border-main accent-violet-500'
              />
              <span className='text-text-secondary text-sm'>{name}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  )
}

export default ClientsDropdown
